import React, { useRef, useState, useEffect } from 'react';
import playIcon from '../assets/video-play-icon.svg';
import pauseIcon from '../assets/video-pause-icon.svg';

const VideoPlayer = ({ videoMaterial, previewTime = 0.1 }) => {
  const videoRef = useRef(null);
  const hideButtonTimeout = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCenterButton, setShowCenterButton] = useState(true);
  const [poster, setPoster] = useState(null);

  // Генерация постера безопасно
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoadedData = async () => {
      try {
        // краткий автоплей в mute, чтобы отрисовался кадр (на iOS это норм)
        const prevMuted = v.muted;
        v.muted = true;
        await v.play().catch(() => {}); // на всякий случай
        v.pause();

        // мягкий seek (если данных мало, просто пропустим)
        const seek = () =>
          new Promise((res) => {
            const onSeeked = () => { v.removeEventListener('seeked', onSeeked); res(); };
            v.addEventListener('seeked', onSeeked);
            try { v.currentTime = previewTime; } catch { res(); }
          });

        await seek();

        // рисуем кадр
        if (v.videoWidth && v.videoHeight) {
          const c = document.createElement('canvas');
          c.width = v.videoWidth;
          c.height = v.videoHeight;
          c.getContext('2d')?.drawImage(v, 0, 0, c.width, c.height);
          setPoster(c.toDataURL('image/jpeg'));
        }

        // возвращаемся в начало
        try { v.currentTime = 0; } catch {}
        v.muted = prevMuted;
      } catch {
        // ничего, постер не обязателен
      }
    };

    v.addEventListener('loadeddata', onLoadedData);
    return () => v.removeEventListener('loadeddata', onLoadedData);
  }, [previewTime]);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      try {
        await v.play();                 // важен await на мобильных
        setIsPlaying(true);
        setShowCenterButton(true);
        if (hideButtonTimeout.current) clearTimeout(hideButtonTimeout.current);
        hideButtonTimeout.current = setTimeout(() => setShowCenterButton(false), 1500);
      } catch (e) {
        // если политика автоплея ругнулась — подскажем включить звук/нажать ещё раз
        console.warn('Play failed:', e);
      }
    } else {
      v.pause();
      setIsPlaying(false);
      setShowCenterButton(true);
    }
  };

  const toggleFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;

    const req =
      v.requestFullscreen ||
      v.webkitEnterFullscreen || // iOS Safari
      v.mozRequestFullScreen ||
      v.msRequestFullscreen;

    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      exit?.call(document);
    } else {
      req?.call(v);
    }
  };

  return (
    <div className="video-container" onClick={togglePlay}>
      <video
        ref={videoRef}
        src={videoMaterial}
        poster={poster || undefined}
        playsInline
        preload="auto"          // чтобы seek/декод прошёл надёжнее
        muted={isMuted}         // пробрасываем состояние mute
        className="styled-video"
      />
      {showCenterButton && (
        <div className="center-button" onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
          {/* когда играет — показываем PAUSE, когда стоит — PLAY */}
          {isPlaying ? <img src={pauseIcon} alt="Pause" /> : <img src={playIcon} alt="Play" />}
        </div>
      )}
      <div className="controls" onClick={(e) => e.stopPropagation()}>
        <button onClick={toggleMute}>{isMuted ? '🔇' : '🔊'}</button>
        <button onClick={toggleFullscreen}>⛶</button>
      </div>
    </div>
  );
};

export default VideoPlayer;
